import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Tune, PrecisionManufacturing } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatDay } from '../components/commercial/contact';

const REC: Record<string, { label: string; color: 'error' | 'warning' | 'success' }> = {
  PRODUCIR_YA: { label: '🔴 Producir ya', color: 'error' },
  PRODUCIR: { label: '🟡 Producir', color: 'warning' },
  NO_PRODUCIR: { label: '🟢 No producir', color: 'success' },
};

/** Cuándo y cuánto producir de cada producto, según ritmo de venta, stock de seguridad y tamaño de lote. */
const ProductionPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'justo' | 'conservador'>('justo');
  const [leadDays, setLeadDays] = useState(15);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getProductionPlan({ mode, leadDays });
      setRows(r.rows);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo calcular el plan');
    } finally {
      setLoading(false);
    }
  }, [mode, leadDays]);

  useEffect(() => { load(); }, [load]);

  const savePlanning = async () => {
    if (!editing) return;
    await api.setProductPlanning(editing.productId, {
      minStock: Number(editing.configuredMinStock) || 0,
      reorderPoint: Number(editing.configuredReorderPoint) || 0,
      productionBatchSize: Number(editing.productionBatchSize) || 0,
    });
    setEditing(null);
    load();
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={800}>Plan de producción</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ritmo de venta de los últimos 60 días. Justo: stock de seguridad de 1 mes. Conservador: toma el mes más alto y 2 meses de seguridad.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="justo">Justo</ToggleButton>
          <ToggleButton value="conservador">Conservador</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" type="number" label="Días que tarda producir" value={leadDays} onChange={e => setLeadDays(Number(e.target.value) || 0)} sx={{ width: 190 }} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Prom. mes</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Tendencia</TableCell>
                  <TableCell align="right">Meses de stock</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Se agota</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Seguridad</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Punto de pedido</TableCell>
                  <TableCell align="right">A producir</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.productId} hover>
                    <TableCell><b>{r.productName}</b></TableCell>
                    <TableCell align="right">{r.currentStock}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{r.monthlyAvg}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {r.trend == null ? '—' : <span style={{ color: r.trend >= 0 ? '#16a34a' : '#dc2626' }}>{r.trend > 0 ? '+' : ''}{r.trend}%</span>}
                    </TableCell>
                    <TableCell align="right">{r.monthsOfStock ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{r.stockOutAt ? formatDay(r.stockOutAt) : '—'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{r.safetyStock}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{r.reorderPoint}</TableCell>
                    <TableCell align="right"><b>{r.toProduce || '—'}</b>{r.productionBatchSize > 0 && r.toProduce > 0 ? <Typography variant="caption" display="block" color="text.secondary">lotes de {r.productionBatchSize}</Typography> : null}</TableCell>
                    <TableCell><Chip size="small" label={REC[r.recommendation].label} color={REC[r.recommendation].color} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" startIcon={<Tune />} onClick={() => setEditing({ ...r })}>Ajustar</Button>
                        {r.toProduce > 0 && r.hasRecipe && (
                          <Button size="small" variant="contained" color="warning" startIcon={<PrecisionManufacturing />}
                            onClick={() => navigate(`/m/producir?productId=${r.productId}&qty=${r.toProduce}`)}>
                            Producir
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary">No hay productos con control de stock.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajustar · {editing?.productName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField type="number" label="Stock de seguridad (mínimo)" helperText="0 = automático (1 mes de venta)" value={editing?.configuredMinStock ?? 0}
              onChange={e => setEditing({ ...editing, configuredMinStock: e.target.value })} />
            <TextField type="number" label="Punto de pedido" helperText="0 = automático (seguridad + días de producción)" value={editing?.configuredReorderPoint ?? 0}
              onChange={e => setEditing({ ...editing, configuredReorderPoint: e.target.value })} />
            <TextField type="number" label="Tamaño de lote" helperText="Se produce de a múltiplos de esto. 0 = libre." value={editing?.productionBatchSize ?? 0}
              onChange={e => setEditing({ ...editing, productionBatchSize: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancelar</Button>
          <Button variant="contained" onClick={savePlanning}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionPlanPage;
